import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clusterPath } from "@/api/client";
import { execSessionKeys, queryKeys } from "@/api/queries";
import { SSESelectionAcknowledgementError, streamEvents, type SSETransportState } from "@/api/sse";
import { useAnnounce } from "@/app/Announcer";
import { useT } from "@/i18n/I18nProvider";
import { useDashboardStore } from "@/state/dashboardStore";
import { namespaceSelectionTransport } from "@/state/namespaceSelection";
import { useScopeAuthGeneration } from "@/api/ScopeAuthLifecycle";

/**
 * SSE 신호를 재조회로 바꾸는 코얼레서. (#17)
 * - 첫 신호는 wait(기본 1s) 안에 반영합니다 — 조용한 클러스터에서 즉시성 유지.
 * - 신호가 끊이지 않아도 flush는 minInterval(기본 15s)에 한 번입니다. Pod churn이
 *   상시적인 클러스터에서 250ms마다 화면 전체를 재조회하면 컨트롤·패널이
 *   계속 출렁이고 백엔드도 연속 부하를 받습니다. 자동 갱신(기본 30s)과 같은
 *   차수의 상한을 둡니다.
 */
export function createInvalidationCoalescer(callback: () => void, wait = 1_000, minInterval = 15_000) {
  let timer: number | undefined;
  let lastFlush = Number.NEGATIVE_INFINITY;
  return {
    schedule() {
      if (timer !== undefined) return;
      const delay = Math.max(wait, lastFlush + minInterval - Date.now());
      timer = window.setTimeout(() => {
        timer = undefined;
        lastFlush = Date.now();
        callback();
      }, delay);
    },
    cancel() { if (timer !== undefined) window.clearTimeout(timer); timer = undefined; },
  };
}

/** 세션 변경 신호인지. 본문은 무효화 신호라 kind만 봅니다 — 해석할 수 없으면 아니라고 봅니다. */
export function isExecSessionSignal(data: string | undefined): boolean {
  if (!data) return false;
  try {
    return (JSON.parse(data) as { kind?: string }).kind === "execsession";
  } catch {
    return false;
  }
}

export function shouldInvalidateStreamQuery(
  query: { queryKey: readonly unknown[]; meta?: Record<string, unknown> },
  clusterId: string,
): boolean {
  return query.queryKey.includes(clusterId) && query.meta?.streamInvalidation !== "manual";
}

export function StreamInvalidator({ clusterId, onTransportState }: {
  clusterId?: string;
  onTransportState?: (state: SSETransportState) => void;
}) {
  const client = useQueryClient();
  /* SSE 낭독도 공용 Announcer 하나를 지납니다. 신호 하나하나가 아니라 **실제로
     화면을 갱신한 횟수**만 알립니다 — 코얼레서가 이미 15초에 한 번으로 묶었고,
     이벤트 본문(리소스 이름·사유)은 여기 들어가지 않습니다. (ADR 0025) */
  const announce = useAnnounce();
  const { t } = useT("common");
  const refreshes = useRef(0);
  const generation = useRef(0);
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const selectionStatus = useDashboardStore((state) => state.namespaceSelectionStatus);
  const capability = useDashboardStore((state) => state.namespaceSelectionCapability);
  const selectionGeneration = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const authGeneration = useScopeAuthGeneration();
  const namespaceTransport = selection ? namespaceSelectionTransport(selection, capability) : undefined;
  useEffect(() => {
	const currentGeneration = ++generation.current;
	if (!clusterId || selectionStatus !== "ready" || !namespaceTransport || namespaceTransport.mode === "blocked") return;
	refreshes.current = 0;
	const controller = new AbortController();
	/* 세션 목록 쿼리 키에는 클러스터가 없습니다(admin은 여러 클러스터를 봅니다). 그래서 세션 변경
	   신호를 받았을 때만 따로 무효화합니다 — 모든 신호에 함께 돌리면 Pod churn마다 목록을 다시 받습니다.
		   (ADR 0030 결정 2) */
		let execSessionsChanged = false;
		let dataChanged = false;
		let namespaceMetadataChanged = false;
		const coalescer = createInvalidationCoalescer(() => {
		  if (generation.current !== currentGeneration || controller.signal.aborted) return;
		  if (dataChanged) {
			dataChanged = false;
			void client.invalidateQueries({ predicate: (query) => shouldInvalidateStreamQuery(query, clusterId) });
		  }
		  if (namespaceMetadataChanged) {
			namespaceMetadataChanged = false;
			void client.invalidateQueries({ queryKey: queryKeys.scope(authGeneration) });
			void client.invalidateQueries({ queryKey: ["namespaces", clusterId], exact: false });
			window.dispatchEvent(new Event("dashboard-namespace-metadata"));
		  }
	  if (execSessionsChanged) {
		execSessionsChanged = false;
		void client.invalidateQueries({ queryKey: execSessionKeys.list });
	  }
	  refreshes.current += 1;
	  announce("stream", t("common.live.streamRefreshed", { count: refreshes.current }));
	});
	void streamEvents(clusterPath(clusterId, "events", "stream"), (message) => {
		  if (generation.current !== currentGeneration || controller.signal.aborted) return;
		  if (message.event === "namespace-metadata") {
			namespaceMetadataChanged = true;
			coalescer.schedule();
			return;
		  }
		  if (message.data || message.event === "reset") {
			dataChanged = true;
			if (message.event === "reset") namespaceMetadataChanged = true;
			if (message.event === "reset" || isExecSessionSignal(message.data)) execSessionsChanged = true;
		window.dispatchEvent(new Event("dashboard-stream-message"));
		coalescer.schedule();
	  }
	}, controller.signal, (state) => {
	  if (generation.current === currentGeneration && !controller.signal.aborted) onTransportState?.(state);
	}, namespaceTransport).catch((error) => {
	  if (error instanceof SSESelectionAcknowledgementError && generation.current === currentGeneration) {
		useDashboardStore.getState().markNamespaceSelectionAckLost(selectionGeneration);
	  }
	});
	return () => { generation.current += 1; controller.abort(); coalescer.cancel(); };
  }, [client, clusterId, announce, onTransportState, t, authGeneration, namespaceTransport?.binding, namespaceTransport?.mode, selectionGeneration, selectionStatus]);
  return null;
}
